<template>
	<div
		ref="wrapper"
		class="countdown-circle"
	>
		<div class="countdown-circle__container">
			<!-- Days -->
			<div class="countdown-circle__item" v-if="countDays && countDays > 0">
				<div class="position-relative">
					<div class="countdown-circle__inner">
						<div class="countdown-circle__time" :style="getColorStyle('days', 'time')">
							{{ days }}
						</div>
						<div class="countdown-circle__label" :style="getColorStyle('days', 'label')">
							{{ options.daysLabel }}
						</div>
					</div>
					<svg
						:width="innerSize"
						:height="innerSize"
						xmlns="http://www.w3.org/2000/svg"
					>
						<circle
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="underneathStrokeColor"
							:fill="hoursFillColor"
						/>
						<circle
							:transform="'rotate(-90, '+cx+', '+cy+')'"
							:style="{ strokeDasharray: strokeDasharrayDay, strokeDashoffset: strokeDashoffsetDay }"
							class="circle_animation"
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="getStrokeColorStyle('days')"
							fill="transparent"
						/>
					</svg>
				</div>
			</div>

			<!-- Hours -->
			<div class="countdown-circle__item">
				<div class="position-relative">
					<div class="countdown-circle__inner">
						<div class="countdown-circle__time" :style="getColorStyle('hours', 'time')">
							{{ formattedHours }}
						</div>
						<div class="countdown-circle__label" :style="getColorStyle('hours', 'label')">
							{{ options.hoursLabel }}
						</div>
					</div>
					<svg
						:width="innerSize"
						:height="innerSize"
						xmlns="http://www.w3.org/2000/svg"
					>
						<circle
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="underneathStrokeColor"
							:fill="hoursFillColor"
						/>
						<circle
							:transform="'rotate(-90, '+cx+', '+cy+')'"
							:style="{ strokeDasharray: strokeDasharrayHour, strokeDashoffset: strokeDashoffsetHour }"
							class="circle_animation"
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="getStrokeColorStyle('hours')"
							fill="transparent"
						/>
					</svg>
				</div>
			</div>

			<!-- minutes -->
			<div class="countdown-circle__item">
				<div class="position-relative">
					<div class="countdown-circle__inner">
						<div class="countdown-circle__time" :style="getColorStyle('minutes', 'time')">
							{{ minutes }}
						</div>
						<div class="countdown-circle__label" :style="getColorStyle('minutes', 'label')">
							{{ options.minutesLabel }}
						</div>
					</div>
					<svg
						:width="innerSize"
						:height="innerSize"
						xmlns="http://www.w3.org/2000/svg"
					>
						<circle
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="underneathStrokeColor"
							:fill="minutesFillColor"
						/>
						<circle
							:transform="'rotate(-90, '+cx+', '+cy+')'"
							:style="{ strokeDasharray: strokeDasharrayMinute, strokeDashoffset: strokeDashoffsetMinute }"
							class="circle_animation"
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="getStrokeColorStyle('minutes')"
							fill="transparent"
						/>
					</svg>
				</div>
			</div>

			<div
				class="countdown-circle__item">
				<div class="position-relative">
					<div class="countdown-circle__inner">
						<div class="countdown-circle__time" :style="getColorStyle('seconds', 'time')">
							{{ seconds }}
						</div>
						<div class="countdown-circle__label" :style="getColorStyle('seconds', 'label')">
							{{ options.secondsLabel }}
						</div>
					</div>
					<svg
						:width="innerSize"
						:height="innerSize"
						xmlns="http://www.w3.org/2000/svg"
					>
						<circle
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="underneathStrokeColor"
							:fill="secondsFillColor"
						/>
						<circle
							:transform="'rotate(-90, '+cx+', '+cy+')'"
							:style="{ strokeDasharray: strokeDasharraySecond, strokeDashoffset: strokeDashoffsetSecond }"
							class="circle_animation"
							:r="r"
							:cy="cx"
							:cx="cy"
							:stroke-width="strokeWidth"
							:stroke="getStrokeColorStyle('seconds')"
							fill="transparent"
						/>
					</svg>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {
	getRgbaColor,
} from '@/lib/helper';

export default {
	props: {
		options: Object,
		initialValue: {
			type: Number,
			required: true,
			default: 0,
		},
		countDays: [Number, String],
		countHours: [Number, String],
		countMinutes: [Number, String],
		countSeconds: [Number, String],
	},
	data() {
		return {
			isMounted: false,
			value: this.initialValue,
			labelFontRatio: 0.15,
			numberFontRatio: 0.2,
			baseTime: 0,
			// strokeWidth: 20,
			secondsFillColor: '#fff',
			minutesFillColor: '#fff',
			hoursFillColor: '#fff',
			size: 200,
			padding: 4,
			underneathStrokeColor: 'lightgrey',
			showNegatives: true,
		};
	},
	computed: {
		secondsStep() {
			// eslint-disable-next-line no-nested-ternary
			return this.is_single ? this.steps ? (Math.max(this.steps, this.initialValue)) : this.initialValue : 60;
		},
		hours() {
			const hours = this.countHours;
			return hours;
		},
		formattedHours() {
			return this.hours;
		},
		minutes() {
			return this.countMinutes;
		},
		seconds() {
			return this.countSeconds;
		},
		days() {
			const days = this.countDays;
			return days;
		},
		factor() {
			return this.value >= 0 ? 1 : -1;
		},
		circleLength() {
			return 2 * Math.PI * this.r;
		},
		dayStepLength() {
			return this.circleLength / 365;
		},
		hourStepLength() {
			return this.circleLength / 24;
		},
		minuteStepLength() {
			return this.circleLength / 60;
		},
		secondStepLength() {
			return this.circleLength / 60;
		},
		strokeDasharrayDay() {
			return this.circleLength;
		},
		strokeDasharrayHour() {
			return this.circleLength;
		},
		strokeDasharrayMinute() {
			return this.circleLength;
		},
		strokeDasharraySecond() {
			return this.circleLength;
		},
		strokeDashoffsetDay() {
			return this.circleLength - this.days * this.dayStepLength;
		},
		strokeDashoffsetHour() {
			return this.circleLength - this.formattedHours * this.hourStepLength;
		},
		strokeDashoffsetMinute() {
			return this.circleLength - this.minutes * this.minuteStepLength;
		},
		strokeDashoffsetSecond() {
			return this.circleLength - this.seconds * this.secondStepLength;
		},
		cx() {
			return this.innerSize / 2;
		},
		cy() {
			return this.innerSize / 2;
		},
		r() {
			return (this.innerSize - this.strokeWidth) / 2;
		},
		innerSize() {
			return this.circleSize - this.padding * 2;
		},
		circleSize() {
			const optionsSize = this.options.circleSize;
			const size = optionsSize === 0 && this.isMounted ? this.$refs.wrapper.parentElement.clientHeight : optionsSize;
			return this.hasLabel ? size - size * this.labelFontRatio : size;
		},
		hasLabel() {
			return this.hourLabel !== '' || this.minuteLabel !== '' || this.secondLabel !== '';
		},
		strokeWidth() {
			// eslint-disable-next-line
			return this.options.circleSize * (15/100);
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.isMounted = true;
			if (this.value) {
				const interval = setInterval(() => {
					if (this.paused) {
						return;
					}
					const delta = 1;
					this.value -= delta;
					if (this.value === 0) {
						this.$emit('finish');
					}
					if (this.value <= 0 && !this.showNegatives) {
						this.value = 0;
						clearInterval(interval);
					}
				}, 1000);
			}
		});
	},
	methods: {
		getStyle(color) {
			const style = {};
			style.color = color;
			return style;
		},
		getStrokeColorStyle(field) {
			let color = '#000';
			if (field === 'days') {
				color = this.options && this.options.daysColor
					? getRgbaColor(this.options.daysColor.rgb)
					: '#000';
			} else if (field === 'hours') {
				color = this.options && this.options.hoursColor
					? getRgbaColor(this.options.hoursColor.rgb)
					: '#000';
			} else if (field === 'minutes') {
				color = this.options && this.options.minutesColor
					? getRgbaColor(this.options.minutesColor.rgb)
					: '#000';
			} else if (field === 'seconds') {
				color = this.options && this.options.secondsColor
					? getRgbaColor(this.options.secondsColor.rgb)
					: '#000';
			}
			return color;
		},
		getColorStyle(field, type) {
			const style = {};
			const sizeRatio = type === 'time' ? 15 : 7;
			let color = '#000';
			if (field === 'days') {
				color = this.options && this.options.daysColor
					? getRgbaColor(this.options.daysColor.rgb)
					: '#000';
				style.color = color;
			} else if (field === 'hours') {
				color = this.options && this.options.hoursColor
					? getRgbaColor(this.options.hoursColor.rgb)
					: '#000';
			} else if (field === 'minutes') {
				color = this.options && this.options.minutesColor
					? getRgbaColor(this.options.minutesColor.rgb)
					: '#000';
			} else if (field === 'seconds') {
				color = this.options && this.options.secondsColor
					? getRgbaColor(this.options.secondsColor.rgb)
					: '#000';
			}
			style.color = color;
			style['font-size'] = `${this.options.circleSize * (sizeRatio / 100)}px`;
			return style;
		},
	},
};
</script>

<style lang="scss">
circle{
	-webkit-transition: all 0.5s ease;
	-moz-transition: all 0.5s ease;
	-ms-transition: all 0.5s ease;
	-o-transition: all 0.5s ease;
	transition: all 0.5s ease;
}

.countdown-circle {
	width: auto;
	box-sizing: border-box;
	display: flex;
	justify-content: center;
	align-items: center;

	&__container {
		margin: 0 auto;
		box-sizing: border-box;
		display: flex;
		flex-wrap: wrap;
	}

	&__item {
		float: left;
		direction: ltr;
		position: relative;
		padding: .5rem;
	}

	&__inner {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%,-50%);
		font-weight: bold;
		font-size: 1.5rem;
		text-align: center;
	}

	&__label {
		font-size: .8rem;
		text-align: center;
	}

	&__time {
		font-size: 2rem;
		font-weight: bold;
	}
}

.is-mobile  {
	.countdown-circle {
		&__item {
			width: 100% !important;
			text-align: center;
		}

		// &__inner {
		// 	top: 30%;
		// 	left: 44%;
		// }
	}
}

@media only screen and (max-width: 768px) {
	.countdown-circle {
		&__item {
			width: 100% !important;
		}

		// &__inner {
		// 	top: 30%;
		// 	left: 44%;
		// }
	}
}

</style>
